import { useState, useEffect } from 'react';
import {
  Box, Container, Paper, Typography, Grid, Avatar, Chip, Button,
  TextField, IconButton, Alert, CircularProgress, Divider
} from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon, PhotoCamera, Close } from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../api/config';
import authService from '../../api/auth';

const StudentProfile = () => {
  const { user, updateUser } = useAuth();
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(user?.photoURL || '');

  const [form, setForm] = useState({
    displayName: user?.displayName || '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phoneNumber: user?.phoneNumber || '',
    level: user?.level || '',
  });

  useEffect(() => {
    const refresh = async () => {
      if (user?.id) {
        try {
          const refreshed = await authService.refreshUserData(user.id);
          if (refreshed) {
            updateUser(refreshed);
            setForm({
              displayName: refreshed.displayName || '',
              firstName: refreshed.firstName || '',
              lastName: refreshed.lastName || '',
              phoneNumber: refreshed.phoneNumber || '',
              level: refreshed.level || '',
            });
            setPhotoPreview(refreshed.photoURL || '');
          }
        } catch (e) { /* ignore */ }
      }
    };
    refresh();
  }, [user?.id]);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      let photoURL = user?.photoURL;
      if (photoFile) {
        const reader = new FileReader();
        photoURL = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(photoFile);
        });
      }

      const updates = {
        displayName: form.displayName || `${form.firstName} ${form.lastName}`.trim(),
        firstName: form.firstName,
        lastName: form.lastName,
        phoneNumber: form.phoneNumber,
        level: form.level,
      };
      if (photoURL) updates.photoURL = photoURL;

      // Remove undefined values
      const clean = Object.fromEntries(Object.entries(updates).filter(([_, v]) => v !== undefined));

      await updateDoc(doc(db, 'users', user.id), clean);
      await updateUser({ ...user, ...clean });
      setSuccess('Profile updated!');
      setEditing(false);
      setPhotoFile(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Not specified';
    return new Date(date).toLocaleDateString('en', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FAFAFA', pt: { xs: 10, sm: 12 }, pb: 4 }}>
      <Container maxWidth="sm">
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Paper sx={{ p: 4, border: '1px solid #eee' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" fontWeight={700}>My Profile</Typography>
            {!editing ? (
              <Button startIcon={<EditIcon />} onClick={() => setEditing(true)}
                sx={{ color: '#F5A623', fontWeight: 600 }}>
                Edit
              </Button>
            ) : (
              <Button startIcon={<Close />} onClick={() => setEditing(false)} color="inherit">
                Cancel
              </Button>
            )}
          </Box>

          {/* Avatar */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
              <Avatar
                src={photoPreview}
                sx={{ width: 110, height: 110, mx: 'auto', bgcolor: '#F5A623', color: '#fff', fontSize: '2.5rem' }}
              >
                {(form.firstName || user?.displayName || 'S').charAt(0).toUpperCase()}
              </Avatar>
              {editing && (
                <IconButton
                  component="label"
                  sx={{
                    position: 'absolute', bottom: 0, right: -5,
                    bgcolor: '#F5A623', color: '#fff', width: 36, height: 36,
                    '&:hover': { bgcolor: '#e09500' }
                  }}
                >
                  <PhotoCamera fontSize="small" />
                  <input type="file" hidden accept="image/*" onChange={handlePhotoChange} />
                </IconButton>
              )}
            </Box>
            {!editing && (
              <>
                <Typography variant="h5" fontWeight={700} sx={{ mt: 2 }}>
                  {user?.displayName || 'Student'}
                </Typography>
                <Typography variant="body2" color="text.secondary">{user?.email}</Typography>
                <Chip
                  label={user?.isPro ? 'PRO' : 'FREE'}
                  size="small"
                  sx={{
                    mt: 1,
                    bgcolor: user?.isPro ? '#FFF3E0' : '#f5f5f5',
                    color: user?.isPro ? '#F5A623' : '#888',
                    fontWeight: 600
                  }}
                />
              </>
            )}
          </Box>

          <Divider sx={{ mb: 3 }} />

          {editing ? (
            /* Edit Mode */
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField fullWidth label="First Name" value={form.firstName}
                  onChange={(e) => setForm(p => ({ ...p, firstName: e.target.value }))} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Last Name" value={form.lastName}
                  onChange={(e) => setForm(p => ({ ...p, lastName: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Display Name" value={form.displayName}
                  onChange={(e) => setForm(p => ({ ...p, displayName: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Phone Number" value={form.phoneNumber}
                  onChange={(e) => setForm(p => ({ ...p, phoneNumber: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Level" value={form.level}
                  onChange={(e) => setForm(p => ({ ...p, level: e.target.value }))}
                  placeholder="e.g. Beginner, Intermediate, Advanced" />
              </Grid>
              <Grid item xs={12}>
                <Button
                  fullWidth variant="contained" onClick={handleSave} disabled={saving}
                  startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
                  sx={{ py: 1.3, bgcolor: '#F5A623', '&:hover': { bgcolor: '#e09500' }, fontWeight: 600 }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Grid>
            </Grid>
          ) : (
            /* View Mode */
            <Box>
              {[
                { label: 'First Name', value: user?.firstName },
                { label: 'Last Name', value: user?.lastName },
                { label: 'Phone', value: user?.phoneNumber },
                { label: 'Level', value: user?.level },
                { label: 'Member since', value: formatDate(user?.createdAt) },
              ].map((item, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                  <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                  <Typography variant="body2" fontWeight={500}>{item.value || 'Not specified'}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default StudentProfile;
